import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { fixCloudinaryUrl } from '../../utils/cloudinaryUrlFixer';
import { getCachedImageUrl } from '../../utils/simpleImageCache';
import { getOptimizedImageUrl, type CloudinaryOptions } from '../../utils/cloudinaryOptimizer';
import { normalizeCloudinaryBaseUrl } from '@/utils/cloudinaryCanonical';

// V160.13: PRODUCTION - Clean logging with stats tracking
const activeImageRequests = new Map<string, Promise<string>>();
const imageCache = new Map<string, string>();

// Stats for monitoring
let totalRequests = 0;
let cacheHits = 0;
let promiseShares = 0;
let newRequests = 0;

// Global function - defined once, never recreated
const loadImageGlobal = async (url: string): Promise<string> => {
  // V325.22: Normalize before caching to ensure canonical base
  const canonicalUrl = url.startsWith('blob:') ? url : normalizeCloudinaryBaseUrl(url);

  totalRequests++;
  const urlShort = url.substring(url.lastIndexOf('/') + 1, url.lastIndexOf('/') + 25);

  // Return cached result immediately
  if (imageCache.has(canonicalUrl)) {
    cacheHits++;
    if (process.env.NODE_ENV === 'development') {
      console.log(`♻️ V160.13 [${totalRequests}]: Cache hit ${urlShort} (${cacheHits}/${totalRequests} = ${Math.round(cacheHits / totalRequests * 100)}%)`);
    }
    return imageCache.get(canonicalUrl)!;
  }

  // Return existing promise if already loading
  if (activeImageRequests.has(canonicalUrl)) {
    promiseShares++;
    if (process.env.NODE_ENV === 'development') {
      console.log(`⏳ V160.13 [${totalRequests}]: Promise share ${urlShort} (${promiseShares} shares prevented duplicate requests)`);
    }
    return activeImageRequests.get(canonicalUrl)!;
  }

  // Create new request only if none exists
  newRequests++;
  if (process.env.NODE_ENV === 'development') {
    console.log(`🆕 V160.13 [${totalRequests}]: NEW request ${urlShort} (${newRequests} total network requests)`);
  }

  const promise = getCachedImageUrl(canonicalUrl).then(result => {
    imageCache.set(canonicalUrl, result);
    activeImageRequests.delete(canonicalUrl);
    return result;
  }).catch(error => {
    activeImageRequests.delete(canonicalUrl);
    throw error;
  });

  activeImageRequests.set(canonicalUrl, promise);
  return promise;
};

// Export stats for monitoring
export const getImageLoadStats = () => ({
  totalRequests,
  cacheHits,
  promiseShares,
  newRequests,
  cacheHitRate: totalRequests > 0 ? Math.round(cacheHits / totalRequests * 100) : 0,
  deduplicationRate: totalRequests > 0 ? Math.round((cacheHits + promiseShares) / totalRequests * 100) : 0
});

// V325.3: Module-level avatar URL cache — survives component remounts within same session
// Prevents flash/shimmer when switching back to Network tab (avatars appear instantly)
const avatarOptimizedUrlCache = new Map<string, string>();

function getAvatarCacheKey(userId: string, sourceUrl: string, sizeValue: number): string {
  return `${userId}|${sourceUrl}|${sizeValue}`;
}

interface UserAvatarProps {
  userId?: string;
  user?: { id?: string; name?: string; photo_urls?: string[] };
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | number;
  className?: string;
  showOnlineStatus?: boolean;
  onClick?: () => void;
  /** V325.3: 'eager' for connection cards (visible on tab switch) — prevents lazy-load flash */
  imageLoading?: 'lazy' | 'eager';
}

const UserAvatar: React.FC<UserAvatarProps> = ({
  userId,
  user,
  size = 'md',
  className = '',
  showOnlineStatus = false,
  onClick,
  imageLoading = 'lazy'
}) => {
  const { isReady, isLoading, user: authUser } = useAuth();
  const [imageError, setImageError] = useState(false);

  const sizeValue = typeof size === 'number' ? size :
    size === 'xs' ? 24 : size === 'sm' ? 32 : size === 'md' ? 40 :
      size === 'lg' ? 48 : size === 'xl' ? 64 : 40;

  // ABSOLUTE CLOUDINARY BLOCKING until authentication complete
  const canLoadImages = isReady && !isLoading && authUser?.id;
  const targetUserId = userId || user?.id || authUser?.id;
  const originalUrl = user?.photo_urls?.[0];

  // V325.3: Use cached optimized URL as initial state when available — instant display on remount
  const cacheKey = originalUrl && targetUserId ? getAvatarCacheKey(targetUserId, originalUrl, sizeValue) : null;
  const [optimizedImageUrl, setOptimizedImageUrl] = useState<string>(() => {
    // V325.23: Check module-level cache first (instant on remount)
    if (cacheKey && avatarOptimizedUrlCache.has(cacheKey)) {
      return avatarOptimizedUrlCache.get(cacheKey)!;
    }
    // V325.25: Remove canLoadImages gate for sync compute — this is a pure URL
    // string transform, not a network request. Safe to run before auth settles.
    if (originalUrl && !originalUrl.startsWith('blob:')) {
      const canonical = normalizeCloudinaryBaseUrl(originalUrl);
      const optimized = getOptimizedImageUrl(canonical, {
        width: sizeValue,
        height: sizeValue,
        crop: 'fill',
        gravity: 'faces',
        quality: 'auto',
        format: 'webp',
      });
      if (cacheKey) avatarOptimizedUrlCache.set(cacheKey, optimized);
      return optimized;
    }
    return '/default-avatar.svg';
  });
  // V325.25: Also mark loaded when sync compute produced a URL
  const [imageLoaded, setImageLoaded] = useState(() =>
    !!(cacheKey && avatarOptimizedUrlCache.has(cacheKey)) ||
    !!(originalUrl && !originalUrl.startsWith('blob:'))
  );

  useEffect(() => {
    // Check if we have a photo URL and user ID
    if (!originalUrl || !targetUserId || !canLoadImages) {
      setOptimizedImageUrl('/default-avatar.svg');
      return;
    }

    const key = getAvatarCacheKey(targetUserId, originalUrl, sizeValue);

    // V325.3: Sync cache hit — instant display, no async, no flash
    if (avatarOptimizedUrlCache.has(key)) {
      setOptimizedImageUrl(avatarOptimizedUrlCache.get(key)!);
      return;
    }

    const loadOptimizedImage = async () => {
      try {
        const options: CloudinaryOptions = {
          width: sizeValue,
          height: sizeValue,
          crop: 'fill',
          gravity: 'faces',
          quality: 'auto',
          format: 'webp',
        };

        // V325.22: Normalize to canonical base before optimizing.
        // Prevents double-transforms when source URL already contains transforms.
        const canonicalUrl = originalUrl.startsWith('blob:')
          ? originalUrl
          : normalizeCloudinaryBaseUrl(originalUrl);
        const optimizedUrl = await getCachedImageUrl(canonicalUrl, targetUserId, options);
        avatarOptimizedUrlCache.set(key, optimizedUrl);
        setOptimizedImageUrl(optimizedUrl);
      } catch (error) {
        console.error('❌ V160.14: UserAvatar optimization failed:', error);
        setOptimizedImageUrl('/default-avatar.svg');
      }
    };

    loadOptimizedImage();
  }, [originalUrl, targetUserId, sizeValue, canLoadImages]);

  // V160.14.3: Update image loading state based on optimizedImageUrl
  useEffect(() => {
    if (optimizedImageUrl === '/default-avatar.svg') {
      setImageLoaded(false);
      setImageError(false);
    } else {
      setImageLoaded(true);
      setImageError(false);
    }
  }, [optimizedImageUrl]);

  // Show enhanced placeholder during authentication
  if (!canLoadImages) {
    return (
      <div
        className={`user-avatar placeholder ${className}`}
        style={{
          width: sizeValue,
          height: sizeValue,
          backgroundColor: '#f1f5f9',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#64748b',
          fontSize: sizeValue * 0.4,
          cursor: onClick ? 'pointer' : 'default',
          border: '2px solid #e2e8f0',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
          fontWeight: '500'
        }}
        onClick={onClick}
      >
        {user?.name ? user.name.charAt(0).toUpperCase() : '👤'}
      </div>
    );
  }

  if (!targetUserId) {
    return (
      <div
        className={`user-avatar no-user ${className}`}
        style={{
          width: sizeValue,
          height: sizeValue,
          backgroundColor: '#f8fafc',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#64748b',
          fontSize: sizeValue * 0.4,
          cursor: onClick ? 'pointer' : 'default',
          border: '2px solid #e2e8f0',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
          fontWeight: '500'
        }}
        onClick={onClick}
      >
        ?
      </div>
    );
  }

  // Use the memoized imageUrl (already calculated above)

  return (
    <div
      className={`user-avatar ${className}`}
      style={{ position: 'relative', display: 'inline-block' }}
      onClick={onClick}
    >
      {imageLoaded && !imageError && optimizedImageUrl !== '/default-avatar.svg' ? (
        <img
          src={optimizedImageUrl}
          alt={user?.name ? `${user.name}'s avatar` : "User Avatar"}
          loading={imageLoading}
          decoding="async"
          style={{
            width: sizeValue,
            height: sizeValue,
            borderRadius: '50%',
            objectFit: 'cover',
            border: '2px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
          }}
          onError={() => setImageError(true)}
        />
      ) : (
        <div
          style={{
            width: sizeValue,
            height: sizeValue,
            backgroundColor: imageError ? '#fef2f2' : '#f8fafc',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: imageError ? '#dc2626' : '#64748b',
            fontSize: sizeValue * 0.4,
            border: imageError ? '2px solid #fecaca' : '2px solid #e2e8f0',
            boxShadow: imageError ? '0 2px 8px rgba(220, 38, 38, 0.1)' : '0 2px 8px rgba(0, 0, 0, 0.05)',
            fontWeight: '500'
          }}
        >
          {imageError ? '⚠️' : (user?.name ? user.name.charAt(0).toUpperCase() : '👤')}
        </div>
      )}

      {showOnlineStatus && (
        <div
          style={{
            position: 'absolute',
            bottom: 0,
            right: 0,
            width: sizeValue * 0.25,
            height: sizeValue * 0.25,
            backgroundColor: '#10b981',
            borderRadius: '50%',
            border: '2px solid white'
          }}
        />
      )}
    </div>
  );
};

export default React.memo(UserAvatar, (prev, next) => {
  return prev.userId === next.userId &&
    prev.user?.photo_urls?.[0] === next.user?.photo_urls?.[0] &&
    prev.size === next.size &&
    prev.className === next.className &&
    prev.showOnlineStatus === next.showOnlineStatus &&
    prev.imageLoading === next.imageLoading;
});